import { Box, Theme, Typography, useMediaQuery } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import UndoIcon from "@icons/myicons/arrow-undo.svg?react";
import DepotIconSvg from "@icons/myicons/connection.svg?react";
import { Banner, LoadingButton } from "@components";
import { NoUpdateRequired } from "@components/SynchronizationLog/NoUpdateRequired";
const SCREEN_BREAKPOINT = 720;

export interface AccountHeaderWithRollbackProps {
  onRollback: () => void;
  name: string;
  iban?: string;
  showRollbackLink?: boolean;
  date: string;
  isRollbackLoading: boolean;
  rollbackErrorMessage?: string;
  noData?: boolean;
}

export const AccountHeaderWithRollback = ({
  onRollback,
  name,
  iban,
  date,
  showRollbackLink = true,
  isRollbackLoading,
  rollbackErrorMessage,
  noData,
}: AccountHeaderWithRollbackProps) => {
  const { t } = useTranslation();
  const isPastScreenBreakpoint = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down(SCREEN_BREAKPOINT)
  );

  return (
    <Box display="flex" flexDirection="column" gap={6}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={3}
      >
        <Typography variant="subtitle2" color="textSecondary">
          {format(parseISO(date), "dd.MM.yyyy")} •{" "}
          {format(parseISO(date), "HH:mm")}
        </Typography>
        {showRollbackLink && (
          <LoadingButton
            variant="text"
            size="small"
            color="primary"
            isFetching={isRollbackLoading}
            onClick={() => onRollback()}
            startIcon={<UndoIcon />}
            data-testid="rollback-synchronization-log-button"
          >
            {`${t("synchronizationLog.rollback")}`}
          </LoadingButton>
        )}
      </Box>

      {rollbackErrorMessage && (
        <Banner
          type="error"
          text={rollbackErrorMessage}
          displayDefaultIcon
          mb={3}
        />
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        flexDirection={isPastScreenBreakpoint ? "column" : "row"}
        columnGap={3}
      >
        <Box display="flex" gap={3}>
          <Box display="flex" color="icon.success">
            <DepotIconSvg />
          </Box>
          <div>
            <Typography variant="subtitle2">{name}</Typography>
            {iban && (
              <Typography
                variant="body1"
                fontWeight={600}
                color="textSecondary"
                noWrap
              >
                {iban}
              </Typography>
            )}
          </div>
        </Box>
        {noData && <NoUpdateRequired />}
      </Box>
    </Box>
  );
};
